import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw, Download, Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const fmtDT = (v) => { try { return new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return v || "—"; } };
const STATO_BADGE = {
  pagata: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  in_attesa: "bg-amber-500/15 text-amber-400 border border-amber-500/30",
  creata: "bg-zinc-600/20 text-zinc-400 border border-zinc-600/40",
  fallita: "bg-red-500/15 text-red-400 border border-red-500/30",
  annullata: "bg-red-500/15 text-red-400 border border-red-500/30",
  scaduta: "bg-red-500/15 text-red-400 border border-red-500/30",
  rimborsata: "bg-blue-500/15 text-blue-400 border border-blue-500/30",
  stornata: "bg-blue-500/15 text-blue-400 border border-blue-500/30",
};
const PROVIDERS = [["all", "Tutti i provider"], ["bonifico", "Bonifico"], ["satispay", "Satispay"], ["nexi", "Nexi XPay"], ["paypal", "PayPal"]];
const STATI = [["all", "Tutti gli stati"], ["pagata", "Pagata"], ["in_attesa", "In attesa"], ["creata", "Creata"], ["rimborsata", "Rimborsata"], ["fallita", "Fallita"], ["annullata", "Annullata"], ["scaduta", "Scaduta"]];

export default function PaymentTransactions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ date_from: "", date_to: "", provider: "all", stato: "all", q: "" });
  const [refundTx, setRefundTx] = useState(null);
  const [refundAmt, setRefundAmt] = useState("");
  const [refundMotivo, setRefundMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    try { setData((await api.get("/payments/transactions", { params })).data); }
    catch (e) { toast.error("Errore caricamento transazioni"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f.date_from, f.date_to, f.provider, f.stato]);

  const openRefund = (tx) => {
    const residuo = Number(tx.importo) - Number(tx.rimborsato_importo || 0);
    setRefundTx({ ...tx, residuo }); setRefundAmt(residuo.toFixed(2)); setRefundMotivo("");
  };
  const doRefund = async () => {
    if (!refundTx) return;
    const amt = parseFloat(refundAmt);
    setBusy(true);
    try {
      await api.post(`/payments/transactions/${refundTx.id}/refund`, { importo: amt > 0 ? amt : null, motivo: refundMotivo });
      toast.success("Rimborso eseguito");
      setRefundTx(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore rimborso"); }
    finally { setBusy(false); }
  };

  const exportCsv = () => {
    const rows = data?.transazioni || [];
    const head = ["Data", "Provider", "Metodo", "Importo", "Valuta", "Stato", "Rimborsato", "Cliente", "Bici", "Prenotazione", "ID transazione provider"];
    const lines = [head.join(";")].concat(rows.map((t) => [
      fmtDT(t.created_at), t.provider, t.metodo, Number(t.importo).toFixed(2), t.valuta || "EUR", t.stato,
      Number(t.rimborsato_importo || 0).toFixed(2), (t.cliente_nome || "").replace(/;/g, ","), (t.bici || ""),
      t.prenotazione_id || "", t.id_transazione_provider || "",
    ].join(";")));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `transazioni_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  const totali = data?.totali || {};
  const canRefund = (t) => t.stato === "pagata" && (Number(t.importo) - Number(t.rimborsato_importo || 0)) > 0.001;

  return (
    <div data-testid="pay-transactions">
      {/* Filtri */}
      <div className="bg-[#141414] border border-zinc-800 rounded-sm p-4 mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 mb-3"><Filter className="h-3.5 w-3.5" /> Filtri</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div><Label className="text-[10px] uppercase text-zinc-500">Dal</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="tx-filter-from" /></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Al</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="tx-filter-to" /></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Provider</Label>
            <Select value={f.provider} onValueChange={(v) => setF({ ...f, provider: v })}><SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="tx-filter-provider"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800">{PROVIDERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Stato</Label>
            <Select value={f.stato} onValueChange={(v) => setF({ ...f, stato: v })}><SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="tx-filter-stato"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800">{STATI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Cerca</Label>
            <div className="relative mt-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-600" />
              <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="cliente / prenotazione / id" className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9 pl-8" data-testid="tx-filter-q" /></div></div>
        </div>
      </div>

      {/* Totali */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Transazioni</div><div className="text-2xl font-display" data-testid="tx-tot-count">{totali.count ?? 0}</div></div>
        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Incassato</div><div className="text-2xl font-display text-emerald-400" data-testid="tx-tot-incassato">{eur(totali.incassato)}</div></div>
        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Rimborsato</div><div className="text-2xl font-display text-blue-400" data-testid="tx-tot-rimborsato">{eur(totali.rimborsato)}</div></div>
        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Netto</div><div className="text-2xl font-display text-[#f98125]" data-testid="tx-tot-netto">{eur(totali.netto)}</div></div>
      </div>

      <div className="flex justify-end mb-2">
        <button onClick={exportCsv} data-testid="tx-export" className="flex items-center gap-2 rounded-sm border border-zinc-700 px-3 py-2 text-sm hover:bg-zinc-800"><Download className="h-4 w-4" /> Esporta CSV</button>
      </div>

      {/* Tabella */}
      <div className="bg-[#141414] border border-zinc-800 rounded-sm overflow-x-auto">
        {data === null ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#f98125] mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="text-left p-3">Data</th><th className="text-left p-3">Provider</th><th className="text-left p-3">Metodo</th>
              <th className="text-right p-3">Importo</th><th className="text-left p-3">Stato</th><th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">Prenotazione</th><th className="text-left p-3">ID provider</th><th className="text-right p-3">Azioni</th>
            </tr></thead>
            <tbody>
              {(data.transazioni || []).length === 0 && <tr><td colSpan={9} className="p-8 text-center text-zinc-500" data-testid="tx-empty">Nessuna transazione trovata.</td></tr>}
              {(data.transazioni || []).map((t) => (
                <tr key={t.id} className="border-b border-zinc-800/60 hover:bg-white/[0.02]" data-testid={`tx-row-${t.id}`}>
                  <td className="p-3 whitespace-nowrap text-zinc-400">{fmtDT(t.created_at)}</td>
                  <td className="p-3 capitalize">{t.provider}</td>
                  <td className="p-3 capitalize">{t.metodo}</td>
                  <td className="p-3 text-right font-medium">{eur(t.importo)}{Number(t.rimborsato_importo) > 0 && <div className="text-[10px] text-blue-400">-{eur(t.rimborsato_importo)} rimb.</div>}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm ${STATO_BADGE[t.stato] || "bg-zinc-600"}`}>{t.stato}</span></td>
                  <td className="p-3" data-testid={`tx-cliente-${t.id}`}>
                    <div className="font-medium text-zinc-200">{t.cliente_nome || "—"}</div>
                    {t.bici && <div className="text-[11px] text-zinc-500">Bici {t.bici}</div>}
                  </td>
                  <td className="p-3">
                    {t.prenotazione_id ? (
                      <button onClick={() => navigate(`/noleggi?rental=${t.prenotazione_id}`)} data-testid={`tx-open-rental-${t.id}`}
                        className="font-mono text-[11px] text-[#4da3ff] hover:text-[#7db8ff] hover:underline" title="Apri la prenotazione">
                        #{t.prenotazione_id.replace(/^pren_/, "").slice(0, 10)}
                      </button>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="p-3 font-mono text-[11px] text-zinc-500">{t.id_transazione_provider ? t.id_transazione_provider.slice(0, 16) : "—"}</td>
                  <td className="p-3 text-right">
                    {canRefund(t) && <button onClick={() => openRefund(t)} data-testid={`tx-refund-${t.id}`} className="inline-flex items-center gap-1.5 rounded-sm border border-blue-500/40 text-blue-400 px-2.5 py-1.5 text-xs hover:bg-blue-500/10"><RotateCcw className="h-3.5 w-3.5" /> Storna/Rimborsa</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Conferma rimborso */}
      <AlertDialog open={!!refundTx} onOpenChange={(o) => !o && setRefundTx(null)}>
        <AlertDialogContent className="bg-[#141414] border-zinc-800" data-testid="tx-refund-dialog">
          {refundTx && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-blue-400" /> Storna / Rimborsa</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm">
                    <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-3 space-y-1">
                      <div className="flex justify-between"><span className="text-zinc-500">Provider</span><span className="capitalize">{refundTx.provider}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">Cliente</span><span>{refundTx.cliente_nome || "—"}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">Prenotazione</span><span className="font-mono text-[11px]">#{(refundTx.prenotazione_id || "").replace(/^pren_/, "").slice(0, 12)}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">Importo transazione</span><span>{eur(refundTx.importo)}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">Già rimborsato</span><span>{eur(refundTx.rimborsato_importo)}</span></div>
                      <div className="flex justify-between font-semibold"><span className="text-zinc-400">Rimborsabile</span><span className="text-blue-400">{eur(refundTx.residuo)}</span></div>
                    </div>
                    <div><Label className="text-[10px] uppercase text-zinc-500">Importo da rimborsare (max {eur(refundTx.residuo)})</Label>
                      <Input type="number" step="0.01" min="0" max={refundTx.residuo} value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="tx-refund-amount" />
                      <span className="text-[11px] text-zinc-500">Lascia il totale per un rimborso completo, o riducilo per un rimborso parziale.</span></div>
                    <div><Label className="text-[10px] uppercase text-zinc-500">Motivo (facoltativo)</Label>
                      <Input value={refundMotivo} onChange={(e) => setRefundMotivo(e.target.value)} className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="tx-refund-motivo" /></div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-sm" data-testid="tx-refund-cancel">Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={doRefund} disabled={busy || !(parseFloat(refundAmt) > 0) || parseFloat(refundAmt) > refundTx.residuo + 0.001} data-testid="tx-refund-confirm" className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">{busy ? "Esecuzione…" : `Conferma rimborso di ${eur(parseFloat(refundAmt) || 0)}`}</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
